import { saveUserItem, deleteUserItem, getDeviceId } from '../data/userStore';
import { BETA_KEY, getBetaEntitlement } from '../data/entitlements';
import { getAccessToken } from './supabaseClient';
import { getSessionUser, isSignedInUser } from './authAccount';
import { API_URL } from '../config/api';

// Codes d'invitation beta. A la difference de Patreon (row ecrite par le serveur,
// lue par le client), la row `purchase`/`beta` est ECRITE par le client apres
// validation serveur. C'est un verrou produit, pas une securite forte : assume
// pour une beta.
//
// Le code est rattache au porteur, pas seulement a l'appareil : on envoie le JWT
// de la session quand il y en a un (la session anonyme compte, linkIdentity
// garde le meme identifiant quand elle devient un compte). C'est ce qui permet a
// un testeur de retrouver son acces sur un second appareil sans ressaisir son
// code ni consommer une seconde place du cap. Sans session, le serveur retombe
// sur le device_id, comme avant.
//
// La revocation se fait par la re-validation periodique (`verify`) : quand un
// code est desactive en Admin, le serveur repond active:false et le client
// supprime la row. Offline, la grace de 7 jours (cf. hasBetaAccess) fait le travail.


// Re-validation au boot au plus une fois par 24 h. Offline ou en panne, on ne
// touche a rien : la fenetre de grace court.
const VERIFY_THROTTLE_MS = 24 * 60 * 60 * 1000;
const LAST_VERIFY_KEY = 'upscale_beta_last_verify';

// Le code lui-meme, garde hors des donnees user (comme le device_id, cf.
// userStore). Ces donnees-la sont purgees quand la session change de compte :
// un testeur qui saisit son code en invite, puis se connecte a un compte
// existant sur le meme appareil, perdait son acces alors que le serveur
// continuait de reconnaitre l'appareil. Le code survit donc a la purge, et
// restoreBetaEntitlement s'en sert pour reposer la row.
const CODE_KEY = 'upscale_beta_code';

function rememberCode(code) {
  try {
    localStorage.setItem(CODE_KEY, code);
  } catch {
    // stockage indisponible : on perd juste le repli, pas l'acces courant
  }
}

function forgetCode() {
  try {
    localStorage.removeItem(CODE_KEY);
  } catch {
    // rien a nettoyer
  }
}

function rememberedCode() {
  try {
    return localStorage.getItem(CODE_KEY);
  } catch {
    return null;
  }
}

// Le serveur stocke les codes normalises (upper + trim) : on normalise cote client
// avant tout envoi pour que la saisie "  musicfactory " matche "MUSICFACTORY".
export function normalizeCode(raw) {
  return (raw || '').trim().toUpperCase();
}

// Le token est facultatif cote serveur : une session pas encore prete, ou un
// echec de lecture, laisse simplement l'appel anonyme.
async function authHeader() {
  try {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function postPromo(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { ok: res.ok, status: res.status, data };
}

// Ecrit la row beta locale telle que le client la possede : active, la source, le
// code (pour la re-validation), et le verified_at renvoye par le serveur.
function writeBetaRow(code, verifiedAt) {
  saveUserItem('purchase', BETA_KEY, {
    active: true,
    source: 'beta',
    code,
    verified_at: verifiedAt,
  });
}

// Saisie d'un code par l'utilisateur. Normalise, appelle /redeem, et pose la row
// en cas de succes. Message d'erreur neutre : le serveur ne distingue pas
// invalide / inactif / expire / cap atteint (pas d'enumeration).
export async function redeemBetaCode(rawCode) {
  const code = normalizeCode(rawCode);
  if (!code) return { success: false, error: 'invalid' };
  const device_id = getDeviceId();
  try {
    const { ok, data } = await postPromo('/api/promo/redeem', { code, device_id });
    if (ok && data?.active === true) {
      const verifiedAt = data.verified_at || new Date().toISOString();
      writeBetaRow(code, verifiedAt);
      rememberCode(code);
      localStorage.setItem(LAST_VERIFY_KEY, String(Date.now()));
      return { success: true, verifiedAt };
    }
    return { success: false, error: 'invalid' };
  } catch (err) {
    console.warn('[beta] redeem echoue:', err.message);
    return { success: false, error: 'network' };
  }
}

// Lien d'invitation personnel : echange le token du mail contre un jeton de
// connexion. C'est le serveur qui cree ou retrouve le compte, pose la trace du
// rachat et brule le token ; ici, on ne fait que transmettre.
//
// Un token mort (inconnu, expire, deja servi) n'est pas une erreur a montrer :
// c'est le signal de retomber sur la saisie du code, que le mail porte aussi.
// `reason` ('expired' ou 'exhausted') n'arrive que quand le serveur connait le
// token ; la page s'en sert pour dire laquelle des deux, et retombe sur le
// message le plus doux quand elle manque.
export async function claimInviteToken(rawToken) {
  const token = (rawToken || '').trim();
  if (!token) return { success: false, error: 'invalid' };
  const device_id = getDeviceId();
  try {
    const { ok, data } = await postPromo('/api/promo/claim', { token, device_id });
    if (ok && data?.ok === true) {
      // Pas de token_hash = l'appelant etait deja connecte a un compte : le
      // serveur a pose l'acces sur LUI (regle v4) et il n'y a aucune session a
      // ouvrir. L'appelant garde la sienne.
      return { success: true, tokenHash: data.token_hash || null, code: data.code || '' };
    }
    const dead = data?.ok === false && data.error === 'invalid_token';
    return {
      success: false,
      error: dead ? 'invalid' : 'network',
      reason: dead && (data.reason === 'expired' || data.reason === 'exhausted') ? data.reason : null,
    };
  } catch (err) {
    console.warn('[beta] claim echoue:', err.message);
    return { success: false, error: 'network' };
  }
}

// Lecture pure du lien personnel, pour l'interstitiel de choix de compte
// (spec v5 S3, F26) : rend l'adresse invitee et le code sans rien ecrire ni
// consommer. Un lien mort porte sa raison ('expired'/'exhausted'), un token
// inconnu reste muet, une panne remonte 'network' pour que la page distingue
// le repli.
export async function previewInviteToken(rawToken) {
  const token = (rawToken || '').trim();
  if (!token) return { success: false, error: 'invalid', reason: null };
  try {
    const { ok, data } = await postPromo('/api/promo/claim/preview', { token });
    if (ok && data?.ok === true && data.email) {
      return { success: true, email: data.email, code: data.code || '' };
    }
    const dead = data?.ok === false && data.error === 'invalid_token';
    return {
      success: false,
      error: dead ? 'invalid' : 'network',
      reason: dead && (data.reason === 'expired' || data.reason === 'exhausted') ? data.reason : null,
    };
  } catch (err) {
    console.warn('[beta] preview echoue:', err.message);
    return { success: false, error: 'network', reason: null };
  }
}

// Jeton de passage a usage unique (spec v5 S2) : emis par la page de succes
// pour le compte de la SESSION (celui choisi a l'interstitiel), transporte par
// "Open the app" (scheme upscale://) ou par le referrer Play. L'emission exige
// donc une session nommee, le serveur repond 401 sinon. Aucun droit d'acces ne
// voyage ici : l'acces est deja pose cote compte, il arrive par la sync.
export async function issueHandoffToken() {
  try {
    const { ok, data } = await postPromo('/api/promo/handoff', {});
    if (ok && data?.ok === true && data.token) {
      return { success: true, token: data.token, expiresAt: data.expires_at || null };
    }
    return { success: false, error: 'invalid' };
  } catch (err) {
    console.warn('[beta] handoff echoue:', err.message);
    return { success: false, error: 'network' };
  }
}

// Lecture pure du jeton de passage, pour le dialogue de consentement de l'app
// (S4, F10) : "Sign in as {compte} ?" nomme le compte AVANT l'echange, et
// "Not now" ne brule rien. Refus muets, comme tout le handoff : pas de raison.
export async function previewHandoffToken(rawToken) {
  const token = (rawToken || '').trim();
  if (!token) return { success: false, error: 'invalid' };
  try {
    const { ok, data } = await postPromo('/api/promo/handoff/preview', { token });
    if (ok && data?.ok === true && data.email) {
      return { success: true, email: data.email };
    }
    const dead = data?.ok === false && data.error === 'invalid_token';
    return { success: false, error: dead ? 'invalid' : 'network' };
  } catch (err) {
    console.warn('[beta] handoff preview echoue:', err.message);
    return { success: false, error: 'network' };
  }
}

// Echange du jeton de passage contre le jeton de connexion du compte choisi.
// Tout refus serveur est muet et identique (inconnu, expire, deja echange) :
// un jeton mort n'est pas une erreur a montrer, l'app retombe sur le sign-in
// classique.
export async function exchangeHandoffToken(rawToken) {
  const token = (rawToken || '').trim();
  if (!token) return { success: false, error: 'invalid' };
  try {
    const { ok, data } = await postPromo('/api/promo/handoff/exchange', { token });
    if (ok && data?.ok === true && data.token_hash) {
      return { success: true, tokenHash: data.token_hash };
    }
    const dead = data?.ok === false && data.error === 'invalid_token';
    return { success: false, error: dead ? 'invalid' : 'network' };
  } catch (err) {
    console.warn('[beta] handoff exchange echoue:', err.message);
    return { success: false, error: 'network' };
  }
}

// Reconnaissance de l'adresse invitee a la connexion (bonus mail) : le serveur
// matche l'email du JWT contre les invitations et pose la row d'acces sur le
// compte appelant. Appele au boot AVANT le pull (DataProvider), pour que la row
// arrive dans la foulee. No-op en invite, avec un acces deja la, ou sous
// throttle. Le resultat { found, active } sert aux messages "sans acces"
// (found sans active = "Your access has ended").
//
// La meme entree porte le throttle ET le dernier verdict du serveur : le verrou
// s'ouvre longtemps apres le boot, et le plus souvent sur un lancement ou le
// throttle 24 h a empeche tout appel. Sans cette memoire, la surface n'aurait
// rien a lire une fois sur deux. Elle est datee par compte (uid), donc un autre
// compte connecte sur la meme machine ne lit jamais le verdict du precedent.
const MINE_THROTTLE_KEY = 'upscale_invite_mine_at';

function readMineEntry() {
  try {
    return JSON.parse(localStorage.getItem(MINE_THROTTLE_KEY));
  } catch {
    return null;
  }
}

// Dernier { found, active } connu pour CE compte, ou null (jamais interroge,
// autre compte, stockage indisponible).
export function readInvitedAccessStatus(userId) {
  const entry = readMineEntry();
  if (!entry || !userId || entry.uid !== userId) return null;
  if (typeof entry.found !== 'boolean') return null;
  return { found: entry.found, active: entry.active === true };
}

export async function checkInvitedAccess() {
  if (getBetaEntitlement()) return null;
  try {
    const user = await getSessionUser();
    if (!isSignedInUser(user)) return null;
    // Throttle par compte, pas par appareil : la cle survit a switchIdentity,
    // et le compte B qui se connecte sur la meme machine juste apres A doit
    // etre reconnu tout de suite, pas 24 h plus tard.
    const last = readMineEntry();
    if (last && last.uid === user.id && Date.now() - Number(last.at) < VERIFY_THROTTLE_MS) {
      return null;
    }
    const { ok, data } = await postPromo('/api/promo/mine', { device_id: getDeviceId() });
    if (!ok || !data) return null;
    localStorage.setItem(
      MINE_THROTTLE_KEY,
      JSON.stringify({
        at: Date.now(),
        uid: user.id,
        found: data.found === true,
        active: data.active === true,
      })
    );
    return data;
  } catch (err) {
    console.warn('[beta] mine echoue:', err.message);
    return null;
  }
}

// Re-validation au boot online. Throttle 24 h. Ne touche a la row que sur une
// reponse serveur explicite : active:false -> revocation (suppression de la row),
// active:true -> on rafraichit verified_at (reset de la grace). Toute panne
// (offline, 5xx) laisse courir la grace, ne revoque jamais.
export async function refreshBetaEntitlement({ force = false } = {}) {
  const row = getBetaEntitlement();
  if (!row?.code) return null;

  const last = Number(localStorage.getItem(LAST_VERIFY_KEY) || 0);
  if (!force && Date.now() - last < VERIFY_THROTTLE_MS) return null;

  const device_id = getDeviceId();
  try {
    const { ok, data } = await postPromo('/api/promo/verify', { code: row.code, device_id });
    if (!ok || !data) return null;
    localStorage.setItem(LAST_VERIFY_KEY, String(Date.now()));
    if (data.active === false) {
      // Revocation explicite (kill switch Admin) : on retire l'acces ET le repli,
      // sinon chaque boot retenterait un code que le serveur a condamne.
      deleteUserItem('purchase', BETA_KEY);
      forgetCode();
      return { active: false };
    }
    if (data.active === true) {
      writeBetaRow(row.code, data.verified_at || new Date().toISOString());
      rememberCode(row.code);
      return { active: true };
    }
    return null;
  } catch (err) {
    console.warn('[beta] verify echoue:', err.message);
    return null;
  }
}

// Repli quand la row a disparu mais que l'appareil, lui, a bien active un code :
// changement de compte sur cette machine (la connexion a un compte existant
// purge les donnees locales), ou retour en invite apres une deconnexion. Le
// serveur reconnait l'appareil, donc une simple verification suffit a reposer
// l'acces. No-op sans code memorise, ou si une row est deja la.
export async function restoreBetaEntitlement() {
  if (getBetaEntitlement()) return null;
  const code = rememberedCode();
  if (!code) return null;

  const device_id = getDeviceId();
  try {
    const { ok, data } = await postPromo('/api/promo/verify', { code, device_id });
    if (!ok || data?.active !== true) return null;
    writeBetaRow(code, data.verified_at || new Date().toISOString());
    localStorage.setItem(LAST_VERIFY_KEY, String(Date.now()));
    return { active: true };
  } catch (err) {
    console.warn('[beta] restore echoue:', err.message);
    return null;
  }
}

// Deconnexion locale d'un testeur (depuis Settings). Ne previent pas le serveur :
// la redemption reste tracee cote Admin, seul l'acces local est retire. On oublie
// aussi le code : c'est un geste explicite, le repli ne doit pas le rendre.
export function clearBetaEntitlement() {
  deleteUserItem('purchase', BETA_KEY);
  forgetCode();
}
